/**
 * Country-dispatched tax-ID SHAPE validation for `companies.vat_number`.
 *
 * B-new-826 — closes the lockout where `validateSpanishTaxId()` ran on ANY
 * submitted tax ID and routed anything starting with a letter into a Spanish
 * CIF checksum, rejecting every non-Spanish EU VAT number (e.g. German
 * `DE811569869`) as a malformed CIF.
 *
 * ES keeps the existing checksum-verified path (`validateSpanishTaxId`,
 * unchanged — other callers depend on it). Every other EU-27 country gets
 * SHAPE validation only (prefix + length), because Xeito does not implement
 * 26 different national checksum algorithms and getting the shape rule wrong
 * fails in the expensive direction: a false rejection of a valid VAT number
 * is exactly the bug this row exists to fix. Where a country's exact rule is
 * uncertain, the fallback pattern is deliberately permissive — closer to "is
 * this the general shape of an EU VAT number" than a strict per-country
 * checksum. Do NOT tighten this into a checksum library without re-reading
 * this comment: false rejects are worse than false accepts here.
 *
 * EX-number guard (2025 EU SME cross-border exemption scheme): a
 * "Kleinunternehmer-Identifikationsnummer" of the shape `<CC>EX...` (e.g.
 * `DEEX1234567`) certifies an exemption on the HOLDER'S OWN outbound
 * supplies. It is NOT a VAT identification number and must never be treated
 * as one — submitting one here always fails, distinctly from a plain format
 * error, so the employer gets pointed at "you need your VAT ID, not your
 * exemption ID" rather than "your German VAT number is malformed".
 */

import { validateSpanishTaxId } from './spanish-tax-id';

export type TaxIdCountryValidationErrorCode = 'ex_number' | 'invalid_format' | 'unsupported_country';

export interface TaxIdCountryValidation {
  valid: boolean;
  /** 'es' -> checksum-verified Spanish NIF/NIE/CIF. 'eu_vat' -> shape-only EU VAT number. */
  kind: 'es' | 'eu_vat' | null;
  /** Uppercased, whitespace/dash-stripped. For eu_vat this includes the country prefix. */
  normalized: string;
  errorCode: TaxIdCountryValidationErrorCode | null;
}

function stripSeparators(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s.-]/g, '');
}

// Exact shape for the four countries this row's DONE MEANS requires as
// accept cases. Deliberately narrow (real published VAT-number formats),
// unlike the fallback below.
const EXACT_VAT_SHAPES: Record<string, RegExp> = {
  DE: /^DE\d{9}$/,
  FR: /^FR[0-9A-Z]{2}\d{9}$/,
  IT: /^IT\d{11}$/,
  NL: /^NL\d{9}B\d{2}$/,
};

// Every other EU-27 country (ES excluded — handled by validateSpanishTaxId).
// prefix + 2-13 alphanumerics. Deliberately far looser than any real national
// format (the shortest real EU national part is 8 characters) — this is a
// cheap pre-filter, not the authority; VIES is. Do NOT tighten this bound:
// false rejects are the expensive direction, and VIES is the real check
// (see file header). Note: `company.ts`'s `create` persists this shape-only
// result straight to `vat_number` with no verification step, so unverified
// junk that merely matches this loose shape can sit in the column until
// `verifyTaxId` (or never, if the owner skips it) actually confirms it.
const FALLBACK_VAT_SHAPE = /^[A-Z]{2}[0-9A-Z]{2,13}$/;

const EU27_NON_ES_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'SE',
]);

const EU27_CODES = new Set([...EU27_NON_ES_CODES, 'ES']);

/**
 * ISO-3166-1 alpha-2 (as stored on `companies.country`) -> VIES VAT prefix.
 * These are the SAME two-letter code for every EU-27 country except Greece:
 * ISO-3166-1 assigns Greece `GR`, but the VIES VAT-prefix system (which
 * predates and diverges from ISO here) uses `EL` ("Elláda"). This map exists
 * to document that divergence explicitly rather than let the next person
 * assume the two systems are interchangeable — GR is, as far as the EU-27 is
 * concerned, the ONLY entry where `isoToViesPrefix[cc] !== cc`. Every other
 * country is included anyway so a lookup miss is impossible and the
 * intentional identity mappings are visible, not implicit.
 */
const ISO_TO_VIES_PREFIX: Record<string, string> = {
  AT: 'AT', BE: 'BE', BG: 'BG', HR: 'HR', CY: 'CY', CZ: 'CZ', DK: 'DK',
  EE: 'EE', FI: 'FI', FR: 'FR', DE: 'DE', GR: 'EL', HU: 'HU', IE: 'IE',
  IT: 'IT', LV: 'LV', LT: 'LT', LU: 'LU', MT: 'MT', NL: 'NL', PL: 'PL',
  PT: 'PT', RO: 'RO', SK: 'SK', SI: 'SI', SE: 'SE',
};

/** ISO country code -> its VIES VAT prefix. Falls back to the ISO code itself for anything not in the map (there is no such case in the EU-27 today, GR/EL is the sole divergence). */
function isoToViesPrefix(cc: string): string {
  return ISO_TO_VIES_PREFIX[cc] ?? cc;
}

export { isoToViesPrefix };

// All two-letter prefixes that identify a VAT number's issuing country —
// every EU27 ISO code plus VIES's sole divergence (EL for Greece). Used to
// decide "does this raw string ALREADY carry A country prefix" (any valid
// one, not necessarily this company's own) versus "this is a bare,
// unprefixed key" — see the finding-5 fix below for why this distinction
// (rather than "any two letters") matters.
const ALL_VALID_VAT_PREFIXES = new Set([...EU27_CODES, 'EL']);

/**
 * EX-number guard — must run BEFORE the per-country shape check so the
 * error message tells the employer they submitted an exemption ID, not
 * "malformed VAT number for country X".
 *
 * B-new-826 bughunter finding 4: there is no publicly confirmed, canonical
 * layout for the 2025 EU SME cross-border exemption identifier that this
 * codebase could verify from a primary source at the time of writing. Per
 * the finding's own instruction, this guard is therefore deliberately
 * CONSERVATIVE rather than a guess at the "real" layout: it flags `EX`
 * both at the leading position (right after the 2-letter country prefix,
 * e.g. `DEEX1234567`) AND as a trailing suffix (e.g. `ATU12345678EX`),
 * because the fallback shape (`FALLBACK_VAT_SHAPE`) accepts arbitrary
 * letters anywhere and would otherwise silently swallow a suffix-form EX
 * number as an ordinary valid VAT ID. The FR/EX-key collision (a real
 * French VAT number can legitimately have `EX` as its 2-character key,
 * e.g. `FREX123456789`) is exempted from this by `isFrenchVatWithExKey`
 * below, checked at the call site BEFORE this guard's result is acted on.
 * An EX number must never be silently accepted as reverse-chargeable —
 * false rejects here are the safe failure direction, false accepts are not.
 *
 * Irish carve-out check (recorded so the next person doesn't rediscover it):
 * `IE1234567EX` matches `FALLBACK_VAT_SHAPE` (IE has no entry in
 * `EXACT_VAT_SHAPES`) and WOULD be caught by this function's `/EX$/` trailing
 * check. No carve-out is needed, though: the published Irish VAT format
 * restricts the second-from-last character to a letter from a fixed set that
 * excludes forming a real number ending in the two letters `EX` (the
 * trailing-letter positions are constrained to `A` or `H` for the
 * eight/nine-character forms) — the published format never actually
 * produces a string ending in `EX`, so this guard never misfires against a
 * genuine Irish VAT number.
 */
function isExemptionId(normalized: string): boolean {
  return /^[A-Z]{2}EX/.test(normalized) || /EX$/.test(normalized);
}

/**
 * FR/EX collision guard: a French VAT number is `FR` + a 2-character key +
 * 9 digits, and that key CAN legitimately be the letters `EX` (e.g.
 * `FREX123456789`) — that is a real VAT number, not an exemption ID. The
 * general `isExemptionId` guard above would wrongly reject it. This checks
 * the FULL exact French VAT shape (13 chars: `FR` + 2 + 9 digits) before
 * treating an `FREX...` string as an exemption — a genuine EU SME exemption
 * ID (like the `DEEX1234567` example in the file header, 11 chars total)
 * does not have 9 trailing digits, so it still falls through to
 * `isExemptionId` and is correctly rejected. This is narrowly scoped to FR
 * only — every other country's EX guard is untouched.
 */
function isFrenchVatWithExKey(prefixed: string): boolean {
  return EXACT_VAT_SHAPES.FR.test(prefixed);
}

/**
 * Validates a company tax ID for the given (already EU-27-constrained)
 * `country`. Dispatches to the checksum-verified Spanish path for ES, and to
 * shape-only EU VAT validation for every other EU-27 country.
 *
 * Accepts both prefixed (`DE811569869`) and unprefixed (`811569869`) input
 * for the eu_vat path — employers commonly type the bare number — and
 * normalizes to the prefixed form, matching the canonical storage form
 * already declared on `companies.vat_number`.
 */
export function validateTaxIdForCountry(country: string, raw: string): TaxIdCountryValidation {
  const cc = country.trim().toUpperCase();
  const normalized = stripSeparators(raw);

  if (!EU27_CODES.has(cc)) {
    return { valid: false, kind: null, normalized, errorCode: 'unsupported_country' };
  }

  if (cc === 'ES') {
    const result = validateSpanishTaxId(normalized);
    return {
      valid: result.valid,
      kind: result.valid ? 'es' : null,
      normalized: result.normalized,
      errorCode: result.valid ? null : 'invalid_format',
    };
  }

  // Non-ES EU: accept bare numbers by prepending the company's own country
  // prefix before shape-checking, so "811569869" for a DE company validates
  // the same as "DE811569869". This also accepts the VIES-prefixed form for
  // the GR/EL divergence (e.g. "EL123456789" for a GR company) — see
  // ISO_TO_VIES_PREFIX above — since that is the exact prefix the employer
  // sees on the form and the exact prefix VIES itself uses.
  // B-new-826 bughunter finding 5: "is this number already prefixed" must
  // ask "does it start with A VALID COUNTRY prefix (any EU27 ISO code, or
  // the GR/EL VIES divergence)", not "does it start with any two letters".
  // A French VAT number is legitimately alphabetic-first
  // (`FR[0-9A-Z]{2}\d{9}`), so testing against "any two letters" misreads a
  // bare, unprefixed French key like `XX123456789` as already carrying a
  // prefix and corrupts it by skipping the prepend step — reproducing the
  // exact client-accepts/server-rejects bug this row exists to close.
  // Testing against ONLY this company's own prefix would be equally wrong
  // in the other direction: a number genuinely prefixed with a DIFFERENT
  // country's code (e.g. submitting a German number for a French company)
  // must still be recognised as "already prefixed" so the prefix-mismatch
  // check below can reject it distinctly, rather than being misread as
  // unprefixed and mangled into `FR` + `DE123456789`.
  const leadingTwo = normalized.slice(0, 2);
  const hasPrefix = ALL_VALID_VAT_PREFIXES.has(leadingTwo);
  const prefixed = hasPrefix ? normalized : `${cc}${normalized}`;

  if (isExemptionId(prefixed) && !(cc === 'FR' && isFrenchVatWithExKey(prefixed))) {
    return { valid: false, kind: null, normalized: prefixed, errorCode: 'ex_number' };
  }

  // The prefix on the submitted number must agree with the company's own
  // country (by ISO code) OR that country's VIES prefix where the two
  // diverge (GR -> EL) — a mismatched prefix (e.g. FR number on a DE
  // company) is a distinct format error, not silently accepted under the
  // wrong country.
  const submittedPrefix = prefixed.slice(0, 2);
  const viesPrefix = isoToViesPrefix(cc);
  if (submittedPrefix !== cc && submittedPrefix !== viesPrefix) {
    return { valid: false, kind: null, normalized: prefixed, errorCode: 'invalid_format' };
  }

  // Normalize storage to the ISO-consistent form (`companies.country`'s own
  // code as the prefix) regardless of which prefix was submitted — the
  // GR/EL -> VIES translation happens at the VIES call boundary
  // (`splitVatIdentifier`'s caller), not in the stored value.
  const storedForm = `${cc}${prefixed.slice(2)}`;

  const shape = EXACT_VAT_SHAPES[cc] ?? FALLBACK_VAT_SHAPE;
  const valid = shape.test(storedForm);

  return {
    valid,
    kind: valid ? 'eu_vat' : null,
    normalized: storedForm,
    errorCode: valid ? null : 'invalid_format',
  };
}

export { EU27_NON_ES_CODES, EU27_CODES };
